import {FilterSidebar} from "./FilterSidebar.jsx";
import { AutoConvertToggle } from "./common.jsx";
import React, { useMemo, useState, useEffect } from "react";
import {
  useApp,
  useTranslate,
  Icon,
  FontText,
  TypingInput,
  presets,
  eligible,
  read,
} from "./common.jsx";
export function Header() {
  const a = useApp(),
    t = useTranslate();
  return (
    <header>
      <a className="brand" href="#explore">
        <span className="brandmark">ა</span>
        <span>
          Fontlab
          <small>
            {t("Georgian font laboratory", "ქართული შრიფტების ლაბორატორია")}
          </small>
        </span>
      </a>
      <nav aria-label="Main navigation">
        {[
          ["explore", "Explore"],
          ["converter", "Converter"],
          ["packs", "Packs"],
          ["studio", "Preview studio"],
          ["saved", "Saved"],
          ["help", "Help"],
        ].map(([route, label]) => (
          <a
            key={route}
            href={"#" + route}
            className={a.route === route ? "active" : ""}
            onClick={(e) => {
              if (route === "studio") {
                e.preventDefault();
                a.open("studio", a.modal?.id || "905");
              }
            }}
          >
            {route === "saved" && <Icon name="heart" />}
            {label === "Packs" ? t("Packs", "პაკეტები") : t(label)}
            {route === "saved" && (
              <span id="savedcount">{a.saved.length || ""}</span>
            )}
          </a>
        ))}
      </nav>
      <div className="headercontrols">
        <a className="coffee-button" href="https://ko-fi.com/l3vcoffe" target="_blank" rel="noopener noreferrer" aria-label={t("Buy me a coffee", "ყავაზე დამპატიჟე")} data-tooltip={t("Buy me a coffee · Support Fontlab on Ko-fi", "ყავაზე დამპატიჟე · მხარი დაუჭირე Fontlab-ს Ko-fi-ზე")}>
          <Icon name="coffee" /><span>{t("Buy me a coffee", "ყავაზე დამპატიჟე")}</span>
        </a>
        <button
          className="tour-start-button"
          data-start-tour
          title={t("Show me around")}
          aria-label="Show me around"
          onClick={() => a.startTour()}
        >
          ?
        </button>
        <button
          className="languagebutton"
          data-action="language"
          onClick={() => a.setLang(a.lang === "en" ? "ka" : "en")}
        >
          {a.lang === "en" ? "ქარ" : "EN"}
        </button>
        <button
          className="themebutton"
          data-action="theme"
          aria-label="Toggle day and night mode"
          aria-pressed={a.dark}
          onClick={() => a.setDark(!a.dark)}
        >
          <Icon name={a.dark ? "moon" : "sun"} />
          <span>{t(a.dark ? "Night" : "Day")}</span>
        </button>
      </div>
    </header>
  );
}
export function FontCard({ font: f }) {
  const a = useApp(),
    t = useTranslate(),
    saved = a.saved.includes(f.id),
    compared = a.compare.includes(f.id);
  return (
    <article className="fontcard" data-font={f.id}>
      <div className="cardtop">
        <div>
          <button
            className="cardname"
            data-open={f.id}
            onClick={() => a.open("detail", f.id)}
          >
            {f.name}
          </button>
          <div className="author">
            {f.authors.join(", ") || t("Independent designer")}
          </div>
        </div>
        <button
          className={"iconbutton " + (saved ? "on" : "")}
          data-save={f.id}
          aria-label={(saved ? "Unsave " : "Save ") + f.name}
          aria-pressed={saved}
          onClick={() => a.toggleSaved(f.id)}
        >
          <Icon name="heart" />
        </button>
      </div>
      <FontText
        className="specimen"
        font={f}
        text={a.sample || "ქართული დამწერლობა"}
        style={{ fontSize: a.size, color: a.color || "var(--ink)" }}
        role="button"
        tabIndex={0}
        data-open={f.id}
        onClick={() => a.open("detail", f.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            a.open("detail", f.id);
          }
        }}
      />
      <button
        className="samplelink"
        data-sample-open={f.id}
        onClick={() => a.open("studio", f.id)}
      >
        {t("Sample previews")} <span>↗</span>
      </button>
      <div className="cardbottom">
        <div className="tags">
          <span
            className={
              "tag " + (f.encoding.startsWith("Unicode") ? "unicode" : "")
            }
          >
            {f.encoding.replace(" (UTF-8)", "").replace(" (Geo, SP)", "")}
          </span>
          <span className="tag">{t(f.classes[0] || f.style)}</span>
          {!f.file && <span className="tag">{t("Unavailable")}</span>}
        </div>
        <div className="cardactions">
          {eligible(f) && (
            <button
              className="useweb"
              data-embed-add={f.id}
              title={t("Use on the web")}
              onClick={() => a.addEmbed(f.id)}
            >
              <Icon name="code" />
              <span>{t("Web", "ვებ")}</span>
            </button>
          )}
          <button
            data-compare={f.id}
            className={compared ? "on" : ""}
            title={t("Compare fonts", "შრიფტების შედარება")}
            aria-label={"Compare " + f.name}
            aria-pressed={compared}
            onClick={() => a.toggleCompare(f.id)}
          >
            <Icon name="compare" />
          </button>
          {f.file && (
            <a
              href={f.file}
              download={f.filename}
              title={t("Download font")}
              aria-label={"Download " + f.name}
            >
              <Icon name="download" />
            </a>
          )}
        </div>
      </div>
    </article>
  );
}
export function Explorer() {
  const a = useApp(),
    t = useTranslate();
  const [view, setView] = useState("grid"),
    [limit, setLimit] = useState(30),
    [sort, setSort] = useState("featured"),
    [controlsCollapsed, setControlsCollapsed] = useState(read("fontlab-controls-collapsed", false));
  useEffect(() => { try { localStorage.setItem("fontlab-controls-collapsed", JSON.stringify(controlsCollapsed)); } catch {} }, [controlsCollapsed]);
  useEffect(() => { if (a.tour) setControlsCollapsed(false); }, [a.tour]);
  const expandControls = (target = 'search') => {
    setControlsCollapsed(false);
    setTimeout(() => document.getElementById(target)?.focus(), 280);
  };
  const values = (f, k) => (Array.isArray(f[k]) ? f[k] : [f[k]]);
  const options = (k) => {
    const m = new Map();
    a.fonts.forEach((f) =>
      values(f, k)
        .filter(Boolean)
        .forEach((v) => m.set(v, (m.get(v) || 0) + 1)),
    );
    return [...m].sort(([x], [y]) => x.localeCompare(y));
  };
  const filtered = useMemo(() => {
    let rows = a.fonts.filter(
      (f) =>
        (a.route !== "saved" || a.saved.includes(f.id)) &&
        (!a.query ||
          [f.name, f.family, ...f.authors, ...f.classes, ...f.writings, f.id]
            .join(" ")
            .toLowerCase()
            .includes(a.query.toLowerCase())) &&
        Object.entries(a.filters).every(([k, v]) =>
          k === "lari"
            ? !v || f.lari
            : !v.length || values(f, k).some((x) => v.includes(x)),
        ),
    );
    const featured = ["905", "906", "638", "897", "904", "903", "902", "901"];
    const rank = (f) =>
      featured.includes(f.id)
        ? featured.indexOf(f.id)
        : f.encoding.startsWith("Unicode")
          ? 30
          : 100;
    rows.sort(
      sort === "name"
        ? (x, y) => x.name.localeCompare(y.name)
        : sort === "glyphs"
          ? (x, y) => y.symbols - x.symbols
          : (x, y) => rank(x) - rank(y) || x.name.localeCompare(y.name),
    );
    return rows;
  }, [a.fonts, a.saved, a.route, a.query, a.filters, sort]);
  const filter = (k, v, on) => {
    a.setFilters({
      ...a.filters,
      [k]: on
        ? [...(a.filters[k] || []), v]
        : (a.filters[k] || []).filter((x) => x !== v),
    });
    setLimit(30);
  };
  return (
    <div className={"layout " + (a.sidebarCollapsed ? "sidebar-collapsed" : "")}>
<FilterSidebar a={a} options={options} filter={filter} footer={<>893 {t("downloadable fonts")}</>} />
      <main>
        {controlsCollapsed && <div className="controls-summary" aria-label={t("Search and preview summary", "ძებნისა და ნიმუშის შეჯამება")}>
          <button className="controls-expand iconbutton" aria-label={t("Expand search and preview", "ძებნისა და ნიმუშის გაშლა")} aria-expanded={false} aria-controls="explorer-controls" onClick={() => expandControls()}><Icon name="down" /></button>
          <button className="summary-search" data-tooltip={t("Edit search", "ძებნის შეცვლა") + (a.query ? ': ' + a.query : '')} onClick={() => expandControls()}><Icon name="search" /><span>{a.query || t("Search fonts", "შრიფტის ძებნა")}</span></button>
          <span className="summary-divider" />
          <button className="summary-sample" data-tooltip={t("Edit preview text", "ნიმუშის ტექსტის შეცვლა") + ': ' + a.sample} onClick={() => expandControls('sample')}><span className="summary-label">{t(presets[a.preset]?.[0] || "Custom")}</span><span>{a.sample}</span></button>
          <button className="summary-size" data-tooltip={t("Font size", "შრიფტის ზომა")} onClick={() => expandControls('size')}>{a.size}<small>px</small></button>
          <AutoConvertToggle />
        </div>}
        <section id="explorer-controls" className={"explorer-controls " + (controlsCollapsed ? 'collapsed' : '')} inert={controlsCollapsed ? true : undefined}>
        <div className="explorer-controls-inner">
        <div className="titleline explorer-titleline">
            <button className="controls-collapse iconbutton" aria-label={t("Collapse search and preview", "ძებნისა და ნიმუშის შეკეცვა")} aria-expanded={true} aria-controls="explorer-controls" onClick={() => { setControlsCollapsed(true); requestAnimationFrame(() => document.querySelector(".controls-expand")?.focus()); }}><Icon name="up" /></button>
          <h1>{t(a.route === "saved" ? "Good type. Kept close." : "A language. A thousand voices.", a.route === "saved" ? "თქვენი შენახული შრიფტები" : "ქართული შრიფტები. ათასი ხმა.")}</h1>
          <div className="explorer-quick-controls">
            <AutoConvertToggle />
            <button className="explorer-tour-button" data-start-tour onClick={a.startTour}>
              <span>?</span>{t("Show me around")}
            </button>
            <button className="explorer-tour-mobile" data-start-tour onClick={a.startTour}>
              ? {t("Show me around")}
            </button>
          </div>
        </div>
        <div className="searchbar">
          <Icon name="search" />
          <input
            id="search"
            type="search"
            placeholder={t("Search fonts, designers, or styles")}
            value={a.query}
            onChange={(e) => {
              a.setQuery(e.target.value);
              setLimit(30);
            }}
            aria-label="Search fonts"
          />
        </div>
        <div className="previewbar">
          <select
            id="previewpreset"
            className="presetselect"
            value={a.preset}
            onChange={(e) => {
              a.setPreset(e.target.value);
              a.setSample(e.target.value === "custom" ? "" : presets[e.target.value][1]);
              if (e.target.value === "custom")
                document.getElementById("sample")?.focus();
            }}
          >
            {Object.entries(presets).map(([k, v]) => (
              <option key={k} value={k}>
                {t(v[0])}
              </option>
            ))}
          </select>
          <div className="previewfield">
            <TypingInput
              id="sample"
              placeholder={t("Type your own text…", "აკრიფეთ საკუთარი ტექსტი…")}
              value={a.sample}
              onChange={(v) => {
                a.setSample(v);
                a.setPreset("custom");
              }}
              aria-label="Font preview text"
            />
          </div>
          <label className="sizecontrol">
            <input
              id="size"
              type="range"
              min="20"
              max="90"
              value={a.size}
              onChange={(e) => a.setSize(+e.target.value)}
            />
            <span id="sizevalue">{a.size}</span>px
          </label>
          <div className="swatches">
            {["", "#fc5d35", "#4267b2"].map((c) => (
              <button
                key={c}
                className={"swatch " + (a.color === c ? "selected" : "")}
                style={{ background: c || "var(--ink)" }}
                aria-label={(c || "Default") + " preview color"}
                onClick={() => a.setColor(c)}
              />
            ))}
          </div>
        </div>
        </div>
        </section>
        <div className="resultshead">
          <span id="resultcount">
            <b>{filtered.length}</b>{" "}
            {t(a.route === "saved" ? "saved fonts" : "fonts")}
          </span>
          <div className="resulttools">
            <button
              className="mobilefilter"
              data-action="filters"
              onClick={() => a.setFiltersOpen(!a.filtersOpen)}
            >
              <Icon name="filter" />
              {t("Filters")}
            </button>
            <select
              id="sort"
              aria-label="Sort fonts"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              {[
                ["featured", "Featured first"],
                ["name", "Name A–Z"],
                ["glyphs", "Most characters"],
              ].map(([v, l]) => (
                <option key={v} value={v}>
                  {t(l)}
                </option>
              ))}
            </select>
            <div className="viewbuttons">
              {["grid", "list"].map((v) => (
                <button
                  key={v}
                  data-view={v}
                  aria-label={v === "grid" ? "Grid view" : "List view"}
                  className={view === v ? "selected" : ""}
                  onClick={() => setView(v)}
                >
                  <Icon name={v} />
                </button>
              ))}
            </div>
          </div>
        </div>
        <div
          className="fontscroll"
          tabIndex={0}
          aria-label="Font explorer results"
        >
          <div
            id="results"
            className={"grid " + (view === "list" ? "list" : "")}
          >
            {filtered.slice(0, limit).map((f) => (
              <FontCard key={f.id} font={f} />
            ))}
            {!filtered.length && (
              <div className="empty">
                <h2>{t("No fonts match just yet.")}</h2>
                <button
                  className="secondary"
                  onClick={() => {
                    a.setFilters({});
                    a.setQuery("");
                  }}
                >
                  {t("Clear filters")}
                </button>
              </div>
            )}
          </div>
          <button
            id="loadmore"
            className={"loadmore " + (limit >= filtered.length ? "hidden" : "")}
            data-action="more"
            onClick={() => setLimit(limit + 30)}
          >
            {t("Show more fonts")} ({filtered.length - limit})
          </button>
        </div>
      </main>
    </div>
  );
}
